import { useState } from 'react';
import { Search } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import clsx from 'clsx';

function MultiSelect({
  label,
  options,
  selected,
  onChange,
  searchable = false,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
}) {
  const [search, setSearch] = useState('');
  const filtered = searchable
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      {searchable && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="input-field pl-10 py-2 text-sm"
          />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {filtered.map((option) => (
          <button
            key={option}
            onClick={() => toggle(option)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150',
              selected.includes(option)
                ? 'bg-accent/10 border-accent/30 text-accent-300'
                : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300 hover:text-gray-300'
            )}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function TechStackStep() {
  const { techStack, updateTechStack } = useWizardStore();
  const { options, loading } = useWizardOptions();

  const languages = options?.techStack.languages ?? [];
  const frameworksByLang = options?.techStack.frameworksByLang ?? {};
  const databases = options?.techStack.databases ?? [];
  const cloudProviders = options?.techStack.cloudProviders ?? [];
  const infrastructureOptions = options?.techStack.infrastructureOptions ?? [];
  const packageManagers = options?.techStack.packageManagers ?? [];
  const monorepoTools = options?.techStack.monorepoTools ?? [];

  // Derive available frameworks from selected languages
  const availableFrameworks = Array.from(
    new Set(
      techStack.languages.flatMap((lang) => frameworksByLang[lang] || [])
    )
  );

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Tech Stack</h2>
        <p className="text-sm text-gray-500">
          Select the technologies your project uses.
        </p>
      </div>

      {/* Languages */}
      <MultiSelect
        label="Primary Languages"
        options={languages}
        selected={techStack.languages}
        onChange={(languages) => updateTechStack({ languages })}
        searchable
      />

      {/* Frameworks */}
      <MultiSelect
        label="Frameworks"
        options={
          availableFrameworks.length > 0
            ? availableFrameworks
            : Object.values(frameworksByLang).flat().filter((v, i, a) => a.indexOf(v) === i)
        }
        selected={techStack.frameworks}
        onChange={(frameworks) => updateTechStack({ frameworks })}
        searchable
      />

      {/* Databases */}
      <MultiSelect
        label="Databases"
        options={databases}
        selected={techStack.databases}
        onChange={(databases) => updateTechStack({ databases })}
      />

      {/* Cloud Provider */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Cloud Provider
        </label>
        <select
          value={techStack.cloudProvider}
          onChange={(e) => updateTechStack({ cloudProvider: e.target.value })}
          className="select-field"
        >
          <option value="">Select a cloud provider...</option>
          {cloudProviders.map((cp) => (
            <option key={cp} value={cp}>
              {cp}
            </option>
          ))}
        </select>
      </div>

      {/* Infrastructure */}
      <MultiSelect
        label="Infrastructure"
        options={infrastructureOptions}
        selected={techStack.infrastructure}
        onChange={(infrastructure) => updateTechStack({ infrastructure })}
      />

      {/* Package Manager */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Package Manager
        </label>
        <select
          value={techStack.packageManager}
          onChange={(e) => updateTechStack({ packageManager: e.target.value })}
          className="select-field"
        >
          <option value="">Select a package manager...</option>
          {packageManagers.map((pm) => (
            <option key={pm} value={pm}>
              {pm}
            </option>
          ))}
        </select>
      </div>

      {/* Monorepo */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-300">
            Monorepo
          </label>
          <button
            onClick={() =>
              updateTechStack({ monorepo: !techStack.monorepo })
            }
            className={clsx(
              'relative w-11 h-6 rounded-full transition-colors duration-200',
              techStack.monorepo ? 'bg-accent' : 'bg-surface-300'
            )}
          >
            <div
              className={clsx(
                'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200',
                techStack.monorepo && 'translate-x-5'
              )}
            />
          </button>
        </div>
        {techStack.monorepo && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Monorepo Tool
            </label>
            <select
              value={techStack.monorepoTool}
              onChange={(e) =>
                updateTechStack({ monorepoTool: e.target.value })
              }
              className="select-field"
            >
              <option value="">Select a monorepo tool...</option>
              {monorepoTools.map((tool) => (
                <option key={tool} value={tool}>
                  {tool}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
